/**
 * Sélecteur de langue (FR / EN).
 *
 * Deux boutons `aria-pressed` plutôt qu’un menu : cible tactile large, état lisible, et aucun
 * texte codé en dur (les noms de langue viennent du catalogue). Le changement est appliqué à
 * chaud, sans rechargement (DECISIONS §9.3).
 *
 * **Persistance** : depuis M2, la langue est une préférence du compte. Le clic appelle
 * `PATCH /me {locale}` quand une session est ouverte, ce qui la fait retrouver à la reconnexion
 * (le serveur la renvoie dans `user.locale`, appliqué par l’écran de connexion). L’application
 * locale et le cookie `elemora_locale` ont lieu **avant** l’appel réseau : si celui-ci échoue,
 * la langue reste changée pour la session en cours et le repli cookie prend le relais au
 * rechargement. Aucun échec réseau ne bloque donc l’interface.
 */
import { LOCALES, type Locale } from '@elemora/shared/constants';
import { useTranslation } from 'react-i18next';

import { enregistrerLocale, useSessionStore } from '../api';
import { changerLangue, localeCourante } from '../i18n';

export interface ProprietesChoixLangue {
  readonly compact?: boolean;
}

export function ChoixLangue({ compact = false }: ProprietesChoixLangue) {
  const { t } = useTranslation();
  const courante = localeCourante();
  const definirUtilisateur = useSessionStore((etat) => etat.definirUtilisateur);
  const classes = ['choix-langue', compact ? 'choix-langue--compact' : null].filter(Boolean).join(' ');

  /** Applique la langue puis, si un compte est ouvert, la mémorise côté serveur. */
  async function choisir(locale: Locale): Promise<void> {
    // 1. Effet immédiat et repli cookie : l’interface ne dépend pas du réseau.
    await changerLangue(locale);
    if (useSessionStore.getState().statut !== 'connecte') return;
    try {
      // 2. Préférence persistante (DECISIONS §9.3) : le serveur renvoie le compte à jour.
      const reponse = await enregistrerLocale(locale);
      definirUtilisateur(reponse.user);
    } catch {
      // Repli local assumé : le cookie porte déjà la langue. Rien à afficher, rien à bloquer.
    }
  }

  return (
    <div className={classes} role="group" aria-label={t('langue.label')}>
      {LOCALES.map((locale: Locale) => (
        <button
          key={locale}
          type="button"
          className="choix-langue__bouton"
          aria-pressed={courante === locale}
          lang={locale}
          onClick={() => {
            void choisir(locale);
          }}
        >
          {t(`langue.${locale}`)}
        </button>
      ))}
    </div>
  );
}
